import React, { useState, useRef, useEffect } from "react"
import { DateTime } from "luxon"
import { SvgBlackSolidCalendar } from "../../icons"

interface CalendarDetails {
  meetingName: string
  startTime: string
  endTime: string
  timezone: string
}

interface Props {
  meeting: CalendarDetails
}

const AddToCalendarButton: React.FC<Props> = ({ meeting }) => {
  const { meetingName, startTime, endTime, timezone } = meeting
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const start = DateTime.fromJSDate(new Date(startTime)).toUTC()
  const end = DateTime.fromJSDate(new Date(endTime)).toUTC()

  const formatForGoogle = (dt: DateTime) => dt.toFormat("yyyyLLdd'T'HHmmss'Z'")

  const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
    meetingName
  )}&dates=${formatForGoogle(start)}/${formatForGoogle(end)}`

  const downloadICS = () => {
    const formatForICS = (dt: DateTime) => dt.toFormat("yyyyLLdd'T'HHmmss'Z'")

    const icsContent = `
BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Your Company//EN
BEGIN:VEVENT
UID:${Date.now()}@yourdomain.com
DTSTAMP:${formatForICS(DateTime.utc())}
DTSTART:${formatForICS(start)}
DTEND:${formatForICS(end)}
SUMMARY:${meetingName}
END:VEVENT
END:VCALENDAR
    `.trim()

    const blob = new Blob([icsContent], { type: "text/calendar" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${meetingName.replace(/\s+/g, "_")}.ics`
    link.click()
    URL.revokeObjectURL(url)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button onClick={() => setOpen(!open)}>
        <h4 className="text-base font-bold text-graySlate mb-[8px] flex items-center gap-2">
          <SvgBlackSolidCalendar className="w-8 h-8" />
          Add to Calendar ▾
        </h4>
      </button>

      {open && (
        <div className="absolute w-56 bg-darkgrey border rounded shadow-md z-10">
          <a
            href={googleCalendarUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block px-4 py-2 bg-white hover:bg-blurple hover:text-white transition-colors duration-200"
          >
            Google Calendar
          </a>
          <button
            onClick={() => {
              downloadICS()
              setOpen(false)
            }}
            className="block w-full text-left px-4 py-2 bg-white hover:bg-blurple hover:text-white transition-colors duration-200"
          >
            Download .ics
          </button>
        </div>
      )}
    </div>
  )
}

export default AddToCalendarButton
